import React from 'react';
import { TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

export type ValidatedTextFieldProps = Omit<TextFieldProps, 'error' | 'select' | 'variant'> & {
  /** Returns the helper text for an invalid value, or null. Run on blur, then on every change once shown. Never blocks typing. */
  validate: (value: string) => string | null;
  /** Optional tidy-up applied on blur (for example a phone number to (555) 123-4567). */
  normalize?: (value: string) => string;
  /** Calls with the tidied value. Required when `normalize` is set. */
  onNormalized?: (value: string) => void;
};

/**
 * A text field that checks its value when focus leaves it and shows the validator's message as helper text.
 * Typing is never blocked and an empty value is the validator's call.
 */
export const ValidatedTextField: React.FC<ValidatedTextFieldProps> = ({
  validate,
  normalize,
  onNormalized,
  value,
  onBlur,
  helperText,
  ...rest
}) => {
  const [touched, setTouched] = React.useState(false);
  const text = String(value ?? '');
  const message = touched ? validate(text) : null;

  return (
    <TextField
      {...rest}
      value={value}
      error={Boolean(message)}
      helperText={message ?? helperText}
      onBlur={(e) => {
        if (normalize && onNormalized) {
          const next = normalize(text);
          if (next !== text) onNormalized(next);
        }
        setTouched(true);
        onBlur?.(e);
      }}
    />
  );
};
